import { useEffect } from 'react';

import { useChatOverlayContext } from '../context/chatOverlayContext';

/**
 * Custom hook to manage the responsive behavior of the Data Panel
 * based on the screen size (mobile vs desktop).
 *
 * @param {number} [breakpoint=768] - The screen width (in pixels) at which
 * the layout switches between mobile and desktop views. Default is 768px.
 *
 * This hook adds event listeners to handle screen resizing and updates
 * the chat overlay state accordingly.
 *
 * The hook consists of two useEffect calls:
 * 1. Monitors the `chatOverlayState.isDataPanelFullScreen`. If this state is false,
 *    it hides the generated content by updating `showGeneratedContent` to false,
 *    effectively skipping the 60% width for the Data Panel.
 * 2. Monitors the `chatOverlayState.showGeneratedContent`. If this state is true
 *    on mobile screens, it updates `isDataPanelFullScreen` to true to ensure the
 *    Data Panel expands to 100% width, skipping the intermediate 60% width.
 *
 * Note: The event listeners are cleaned up when the component using
 * this hook unmounts.
 */
function useResponsiveDataPanelFullScreen(breakpoint = 768) {
  const { updateChatOverlayState, chatOverlayState } = useChatOverlayContext();

  useEffect(() => {
    // Helper function to handle resizing logic for mobile (< 768px) vs desktop (>= 768px)
    const handleResize = () => {
      const isMobile = window.matchMedia(
        `(max-width: ${breakpoint}px)`
      ).matches;

      if (isMobile) {
        // On mobile, the Data Panel is either 100% or 0%, skip 60% width.
        if (!chatOverlayState.isDataPanelFullScreen) {
          // If showGeneratedContent is true, make Data Panel 100% (isDataPanelFullScreen true)
          updateChatOverlayState('showGeneratedContent', false);
        }
      }
    };

    // Check the initial screen size on mount and handle resize accordingly
    handleResize();

    // Add resize event listener
    window.addEventListener('resize', handleResize);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, [
    breakpoint,
    chatOverlayState.isDataPanelFullScreen,
    updateChatOverlayState,
  ]);

  useEffect(() => {
    // Helper function to handle resizing logic for mobile (< 768px) vs desktop (>= 768px)
    const handleResize = () => {
      const isMobile = window.matchMedia(
        `(max-width: ${breakpoint}px)`
      ).matches;

      if (isMobile) {
        // On mobile, the Data Panel is either 100% or 0%, skip 60% width.
        if (chatOverlayState.showGeneratedContent) {
          // If showGeneratedContent is true, make Data Panel 100% (isDataPanelFullScreen true)
          updateChatOverlayState('isDataPanelFullScreen', true);
        }
      }
    };

    // Check the initial screen size on mount and handle resize accordingly
    handleResize();

    // Add resize event listener
    window.addEventListener('resize', handleResize);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, [
    breakpoint,
    chatOverlayState.showGeneratedContent,
    updateChatOverlayState,
  ]);
}

export default useResponsiveDataPanelFullScreen;
